import UpgradeSubscriptionConfirmModal from "@components/_subscription/business-plan-card/UpgradeSubscriptionConfirmModal"
import { SubscriptionType } from "@components/_subscription/helpers/constants"
import { Button } from "@components/ui/button"
import { Heading } from "@components/ui/typograhpy/Heading"
import { Text } from "@components/ui/typograhpy/Text"
import { ActivePlanType } from "@lib/get-active-plan-type"
import { useGetSubscriptionDetails } from "@lib/hooks/use-get-subscription-details"
import Link from "next/link"
import { useRouter } from "next/router"
import { FC } from "react"
import { PlanCardUsp } from "../PlanCardUsp"
import SubscriptionCongratulation from "../SubscriptionCongratulation"
import { useSubscribeToBusinessPlan } from "./hooks/use-subscribe-to-business-plan"

type BusinessPlanCardPropsVariantStatic = {
  variant: "static"
  activePlanType?: never
}
type BusinessPlanCardPropsVariantDynamic = {
  variant: "dynamic"
  activePlanType: ActivePlanType
}

type BusinessPlanCardProps =
  | BusinessPlanCardPropsVariantStatic
  | BusinessPlanCardPropsVariantDynamic

export const BusinessPlanCard: FC<BusinessPlanCardProps> = ({
  variant,
  activePlanType,
}) => {
  const { push } = useRouter()
  const { data: subscriptionDetails } = useGetSubscriptionDetails()
  const subscriptionDetail = subscriptionDetails[SubscriptionType.BUSINESS]
  const buttonTitle = getButtonTitle()
  const {
    showBasicToBusinessModal,
    setShowBasicToBusinessModal,
    handleClickSubscribe,
    isPending,
  } = useSubscribeToBusinessPlan(activePlanType as ActivePlanType, () =>
    push({
      query: {
        subscription: "success",
        subscribed_plan: "Business",
        purchase_time: new Date().toISOString(),
      },
    }),
  )

  return (
    <div className="bg-purple! relative rounded-md px-5 py-6 md:px-6">
      <Text
        variant="regular"
        className="absolute top-4 right-4 text-[10px]! font-bold text-white uppercase"
      >
        MOST POPULAR
      </Text>
      <div className="mb-6">
        <Heading variant="h6" as="h2">
          Business
        </Heading>
        <Text variant="regular" className="text-shade-400">
          {subscriptionDetail.initialCredit.toLocaleString("nl-nl")} credits
          /month
        </Text>
      </div>
      <div className="mb-6">
        <Heading variant="h4" as="h3">
          {subscriptionDetail.monthlyPrice}
        </Heading>
        <Text variant="regular" className="font-semibold">
          /month
        </Text>
      </div>
      <div className="mb-8">
        {variant === "dynamic" ? (
          <Button
            variant="white"
            width="fill"
            onClick={() => handleClickSubscribe()}
            status={
              isPending
                ? "loading"
                : ["BUSINESS", "ENTERPRISE"].includes(activePlanType)
                  ? "disabled"
                  : "idle"
            }
            className="text-purple"
            leftIcon={activePlanType === "BUSINESS" ? "check" : undefined}
          >
            {buttonTitle}
          </Button>
        ) : (
          <Link
            href="/signup/create-account?redirect_to_stripe=true&subscribed_plan=Business"
            passHref
            legacyBehavior
          >
            <Button
              className="text-purple"
              variant="white"
              width="fill"
              as="link"
            >
              Subscribe
            </Button>
          </Link>
        )}
      </div>
      <ul>
        {subscriptionDetail.usps.map((usp, index) => (
          <PlanCardUsp variant="business" key={index}>
            {usp}
          </PlanCardUsp>
        ))}
      </ul>
      <UpgradeSubscriptionConfirmModal
        open={showBasicToBusinessModal}
        setOpen={setShowBasicToBusinessModal}
        onConfirm={async () => {
          await handleClickSubscribe()
        }}
        loading={isPending}
      />
      <SubscriptionCongratulation />
    </div>
  )

  function getButtonTitle() {
    if (activePlanType === "BUSINESS") {
      return "Current plan"
    }

    if (activePlanType === "FREE" || activePlanType === "BASIC") {
      return "Upgrade plan"
    }

    return "Enterprise plan is active"
  }
}
